import React from 'react';
import useLabelingStore from '../../detectionLabel/store/useDetectionStore';
import Button from '../../../../../components/Atoms/Button.jsx';

/**
 * Toolbar for SegmentationLabel — polygon only, no bounding box.
 * Shares the toolbar styles with DetectionLabel (detectionLabel.scss).
 */
const SegmentationMiddleTop = ({ undo, redo, canUndo, canRedo }) => {
  const {
    setMode,
    mode,
    getCurrentImage,
    startPolygon,
    isDrawingPolygon,
    prevImage,
    currentImageIndex,
    images,
    nextImage,
    zoomIn,
    zoomOut,
    zoomToFit,
    zoomToActual,
    zoomLevel,
    isCrosshairEnabled,
    setCrosshairEnabled,
    showLabels,
    setShowLabels,
    showFill,
    setShowFill,
  } = useLabelingStore();

  const hasImage = !!getCurrentImage();
  const total = images.length;

  return (
    <div className="lf-toolbar">
      <div className="lf-toolbar__group lf-toolbar__group--segmented" role="group" aria-label="Tools">
        <Button
          className={`lf-tool-btn ${mode === 'select' ? 'lf-tool-btn--active' : ''}`}
          onClick={() => setMode('select')}
          tooltipText="Select and edit (V)"
        >
          <i className="fa fa-mouse-pointer" aria-hidden="true"></i>
          <span className="lf-tool-btn__label">Select</span>
        </Button>
        <Button
          className={`lf-tool-btn ${mode === 'polygon' || isDrawingPolygon ? 'lf-tool-btn--active' : ''}`}
          onClick={() => {
            startPolygon();
            setMode('polygon');
          }}
          disabled={!hasImage || isDrawingPolygon}
          tooltipText="Draw segment (G)"
        >
          <i className="fa fa-draw-polygon" aria-hidden="true"></i>
          <span className="lf-tool-btn__label">Segment</span>
        </Button>
        <Button
          className={`lf-tool-btn ${mode === 'pan' ? 'lf-tool-btn--active' : ''}`}
          onClick={() => setMode('pan')}
          tooltipText="Pan the image (H, or hold Space)"
        >
          <i className="fa fa-hand-paper" aria-hidden="true"></i>
          <span className="lf-tool-btn__label">Pan</span>
        </Button>
      </div>

      <div className="lf-toolbar__group" role="group" aria-label="History">
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={() => undo?.()}
          disabled={!canUndo}
          tooltipText="Undo (Ctrl+Z)"
        >
          <i className="fa fa-undo" aria-hidden="true"></i>
        </Button>
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={() => redo?.()}
          disabled={!canRedo}
          tooltipText="Redo (Ctrl+Y)"
        >
          <i className="fa fa-redo" aria-hidden="true"></i>
        </Button>
      </div>

      <div className="lf-toolbar__group" role="group" aria-label="View options">
        <Button
          className={`lf-tool-btn lf-tool-btn--icon ${showLabels ? 'lf-tool-btn--active' : ''}`}
          onClick={() => setShowLabels(!showLabels)}
          tooltipText="Show class names on shapes (L)"
        >
          <i className="fa fa-tag" aria-hidden="true"></i>
        </Button>
        <Button
          className={`lf-tool-btn lf-tool-btn--icon ${showFill ? 'lf-tool-btn--active' : ''}`}
          onClick={() => setShowFill(!showFill)}
          tooltipText="Stronger shape fill (F)"
        >
          <i className="fa fa-fill-drip" aria-hidden="true"></i>
        </Button>
        <Button
          className={`lf-tool-btn lf-tool-btn--icon ${isCrosshairEnabled ? 'lf-tool-btn--active' : ''}`}
          onClick={() => setCrosshairEnabled(!isCrosshairEnabled)}
          tooltipText="Crosshair guides while drawing (C)"
        >
          <i className="fa fa-crosshairs" aria-hidden="true"></i>
        </Button>
      </div>

      <div className="lf-toolbar__spacer" />

      <div className="lf-toolbar__group" role="group" aria-label="Zoom">
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={zoomOut}
          disabled={!hasImage}
          tooltipText="Zoom out (−)"
        >
          <i className="fa fa-search-minus" aria-hidden="true"></i>
        </Button>
        <span className="lf-zoom-readout" title="Current zoom">
          {Math.round(zoomLevel * 100)}%
        </span>
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={zoomIn}
          disabled={!hasImage}
          tooltipText="Zoom in (+)"
        >
          <i className="fa fa-search-plus" aria-hidden="true"></i>
        </Button>
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={zoomToFit}
          disabled={!hasImage}
          tooltipText="Fit image to the panel (0)"
        >
          <i className="fa fa-expand-arrows-alt" aria-hidden="true"></i>
        </Button>
        <Button
          className="lf-tool-btn lf-tool-btn--ratio"
          onClick={zoomToActual}
          disabled={!hasImage}
          tooltipText="Actual size, 100% (1)"
        >
          1:1
        </Button>
      </div>

      <div className="lf-toolbar__group" role="group" aria-label="Image navigation">
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={prevImage}
          disabled={currentImageIndex === 0 || total === 0}
          tooltipText="Previous image (A / ←)"
        >
          <i className="fa fa-angle-left" aria-hidden="true"></i>
        </Button>
        <span className="lf-image-counter">
          {total === 0 ? '0 / 0' : `${currentImageIndex + 1} / ${total}`}
        </span>
        <Button
          className="lf-tool-btn lf-tool-btn--icon"
          onClick={nextImage}
          disabled={currentImageIndex === total - 1 || total === 0}
          tooltipText="Next image (D / →)"
        >
          <i className="fa fa-angle-right" aria-hidden="true"></i>
        </Button>
      </div>

      {isDrawingPolygon && (
        <span className="lf-toolbar__status">
          <i className="fa fa-draw-polygon" aria-hidden="true"></i> Drawing segment…
        </span>
      )}
    </div>
  );
};

export default SegmentationMiddleTop;
